import {
	always,
	and,
	cond,
	endsWith,
	equals,
	filter,
	find,
	includes,
	is,
	isNil,
	keys,
	length,
	map,
	not,
	or,
	propEq,
	replace,
	values,
} from 'ramda';
import React, { FC, ReactNode, useMemo, useState } from 'react';
import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import CardDrawer from '@ui/atoms/CardDrawer';
import FlexColumn from '@ui/atoms/FlexColumn';
import SelectableButton, { ISelectableButtonProps } from '@ui/atoms/SelectableButton';
import SelectableButtonGroup, { ISelectableGroupProps } from '@ui/atoms/SelectableButtonGroup';
import SubheadingText from '@ui/atoms/SubheadingText';

import TextInput from '@ui/forms/TextInput';

import ProductCard, { IProductPrice } from '@ui/molecules/ProductCard';
import ProductSummaryCard from '@ui/molecules/ProductSummaryCard';

import flexible from '@ui/styled/Flexible';
import spaced from '@ui/styled/Spaced';

import { doesNotExist, exists } from '@utils/general';

import { DeliveryTypes } from '@folx-types/delivery';

import theme from '@theme/FolxTheme';

import { IMedPrice, IMedication, IPriceRange, getRoutePriceRange } from './MedicationHelpers';

type medType = 'route' | 'med' | 'type' | 'frequency' | 'dose';

interface IMedicationSelectionProps {
	handleEdit: () => void;
	header: string;
	name: string;
	options: IMedication[];
	type: medType;
	selection?: string | null;
	extraText?: {
		footerText?: string;
		priceText?: string;
	};
	handleAnimating?: (animating: boolean) => void;
	handleSelection?: (value: string) => void;
	parentKey?: string | null;
	prices?: IMedPrice | Record<string, IMedPrice>;
	shouldAnimate?: boolean;
	visible?: boolean;
	delivery?: DeliveryTypes;
}

const FlexibleButtonGroup = flexible<ISelectableGroupProps>(SelectableButtonGroup);
const FlexibleButton = flexible<ISelectableButtonProps>(SelectableButton);

const SpacedSubheading = spaced(SubheadingText);

const StyledTextInput = styled(TextInput)`
	margin-top: ${theme.spacing.xs};

	:focus-within {
		outline-width: 1px;
		margin: ${theme.spacing.xs} 1px 1px;
	}
`;

const ErrorText = styled(BasicText).attrs({
	color: 'red',
	tint: 'darken40',
})`
	margin-bottom: ${theme.spacing.sm};
`;

const Wrapper = styled.div<{ visible: boolean }>`
	opacity: ${({ visible }) => (visible ? 1 : 0)};
	transition: opacity 3ms ease-in;
`;

export const MedicationSelection: FC<IMedicationSelectionProps> = ({
	delivery = 'FOLX',
	extraText,
	handleAnimating = () => {},
	handleEdit = () => {},
	handleSelection = () => {},
	header,
	name,
	options,
	parentKey,
	prices = {},
	selection = '',
	shouldAnimate = true,
	type,
	visible = true,
}) => {
	const {
		control,
		register,
		watch,
		formState: { errors, isDirty },
	} = useFormContext();

	const value = watch(name);
	const [animating, setAnimating] = useState(false);

	const getNameForMedType = () => {
		return cond([
			[equals<medType>('route'), always('Route')],
			[equals<medType>('med'), always('Medication')],
			[equals<medType>('type'), always('Type')],
			[equals<medType>('frequency'), always('Frequency')],
			[equals<medType>('dose'), always('Dosage')],
		])(type);
	};

	const getOptionByKey: (key: string) => IMedication | undefined = (key = '') => {
		const findOption = find<IMedication>(propEq('key', key));
		return findOption(options);
	};

	const filterOptions: (parent?: string) => IMedication[] = (parent) => {
		return exists(parent)
			? filter<IMedication>((option) => {
					if (endsWith('.custom', option.key)) {
						const customOption = replace('.custom', '')(option.key);
						return includes(customOption, parent ?? '');
					}
					return includes(parent ?? '', option.key);
			  })(options)
			: options;
	};

	const findPricesByKey: (key: string) => IMedPrice = (key) => {
		if (isNil(prices)) {
			return {};
		}

		const priceKey = find((priceKey: string) => includes(priceKey, key), keys(prices)) ?? '';
		return prices?.[priceKey] ?? {};
	};

	const renderPriceOption = (
		option: IMedication,
		price: IPriceRange,
		field: ControllerRenderProps
	) => {
		const priceRange: IProductPrice[] = or(isNaN(price?.min), equals(price.max, price.min))
			? [
					{
						value: price.max,
						additional: true,
						finePrint: extraText?.priceText,
					},
			  ]
			: [
					{
						value: price.min,
						interval: price.max,
						additional: true,
						finePrint: extraText?.priceText,
					},
			  ];

		return (
			<SelectableButton
				{...field}
				name={option.key}
				key={option.key}
				alignment={'start'}
				optionValue={option.key}
				optionLabel={option.label}
				onChange={(value) => {
					if (is(Object, value)) {
						return;
					}
					field.onChange(value);
					handleSelection(value);
				}}>
				<ProductCard
					borderless
					headerText={option.label}
					bodyText={option?.description ?? ''}
					name={option.key}
					prices={priceRange}
					footerText={extraText?.footerText}
				/>
			</SelectableButton>
		);
	};
	const renderDescriptionOption = (option: IMedication, field: ControllerRenderProps) => {
		return (
			<FlexibleButton
				{...field}
				name={option.key}
				key={option.key}
				gap="xs"
				alignment={'start'}
				optionValue={option.key}
				optionLabel={option.label}
				onChange={(value) => {
					if (is(Object, value)) {
						return;
					}
					field.onChange(value);
					handleSelection(value);
				}}>
				<ProductCard
					borderless
					headerText={option.label}
					name={option.key}
					bodyText={option?.description ?? ''}
				/>
			</FlexibleButton>
		);
	};
	const renderBasicOption = (option: IMedication, field: ControllerRenderProps) => {
		return (
			<SelectableButton
				{...field}
				name={option.key}
				key={option.key}
				alignment={'start'}
				optionValue={option.key}
				onChange={(value) => {
					if (is(Object, value)) {
						return;
					}
					field.onChange(value);
					handleSelection(value);
				}}>
				{option.label}
			</SelectableButton>
		);
	};

	const renderUnpricedOption = (option: IMedication, field: ControllerRenderProps) => {
		if (exists(option.description)) {
			return renderDescriptionOption(option, field);
		}
		return renderBasicOption(option, field);
	};

	const renderOptions = useMemo(
		() => (options: IMedication[], field: ControllerRenderProps) => {
			return map<IMedication, ReactNode>((option) => {
				if (delivery !== 'FOLX') {
					return renderUnpricedOption(option, field);
				}

				const optionPrices = is(Number, prices?.[option.key])
					? [prices?.[option.key]]
					: values(findPricesByKey(option.key));
				const priceRange = length(optionPrices) && getRoutePriceRange(optionPrices);
				if (and(!!priceRange, exists(priceRange))) {
					return renderPriceOption(option, priceRange, field);
				}

				return renderUnpricedOption(option, field);
			})(options);
		},
		[parentKey, prices]
	);

	const renderSelectedOption: (key: string) => ReactNode = (key) => {
		const CUSTOM = 'Custom';
		const customKey = '.custom';
		const isCustom = includes(customKey);
		const inputName = `${name}${CUSTOM}`;
		const option = getOptionByKey(key);
		const disabled = doesNotExist(selection) && not(animating);
		return option && isCustom(key) ? (
			<FlexColumn gap="sm">
				<ProductSummaryCard
					label={disabled ? '' : 'Edit'}
					disabled={disabled}
					name={option.key}
					header={option.label}
					subheader={option.description}
					handleClick={() => {
						handleEdit();
					}}
				/>
				<StyledTextInput
					placeholder="Specify"
					label={`${CUSTOM} ${getNameForMedType()}`}
					{...register(inputName)}
					error={errors && errors[inputName]}
					errorLabel={errors[inputName]?.message ?? undefined}
				/>
			</FlexColumn>
		) : (
			<ProductSummaryCard
				label={disabled ? 'N/A' : 'Edit'}
				disabled={disabled}
				name={option?.key ?? ''}
				header={option?.label ?? ''}
				subheader={option?.description}
				handleClick={() => {
					handleEdit();
				}}
			/>
		);
	};

	return (
		<Wrapper visible={visible}>
			{visible && <SpacedSubheading marginBottom="sm">{header}</SpacedSubheading>}
			{visible && errors && errors[name] && errors[name].message && (
				<ErrorText>{errors[name].message}</ErrorText>
			)}
			<CardDrawer
				shouldAnimate={shouldAnimate}
				handleAnimating={(isAnimating) => {
					if (not(shouldAnimate)) {
						return;
					}
					handleAnimating(isAnimating);
					setAnimating(isAnimating);
				}}
				expanded={doesNotExist(selection)}
				collapsedContent={renderSelectedOption(selection)}>
				<Controller
					name={name}
					control={control}
					shouldUnregister={false}
					render={({ field }) => (
						<FlexibleButtonGroup gap="sm" name={name} layout="column">
							{renderOptions(filterOptions(parentKey), field)}
						</FlexibleButtonGroup>
					)}
				/>
			</CardDrawer>
		</Wrapper>
	);
};

export default MedicationSelection;
